import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Drawer, IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

import useCheckoutDrawerState from './useCheckoutDrawerState';
import { useStyles } from './checkoutDrawer.styles';
import ProcessingView from './steps/ProcessingView';
import CheckoutForm from './steps/CheckoutForm';
import ThreeDSecureView from './threeDSecure';

function CheckoutDrawer ({
    open, plan, addOnValues, onClose, onSuccess,
    subscribeAction, onSubscribed, previewAction,
    addOnsOnly, threeDSecureStore, finalize3DSecureAction,
    title, productLabel, showBasePrice, showPromo, payLabel,
    onScheduleConflict,
}) {
    const classes = useStyles();
    const state = useCheckoutDrawerState({ open, plan, addOnValues, onClose, onSuccess, subscribeAction, onSubscribed, previewAction, addOnsOnly, threeDSecureStore, finalize3DSecureAction, onScheduleConflict });
    const { step, handleCancel, actionURL } = state;

    if (!plan) return null;

    return (
        <React.Fragment>
            <Drawer
                anchor="right"
                open={open}
                onClose={step === 'processing' || step === 'threeDSecure' ? undefined : handleCancel}
                PaperProps={{ className: classes.drawerPaper }}
                aria-label="Checkout drawer"
            >
                {/* Header */}
                <div className={classes.header}>
                    <div className={classes.headerLabels}>
                        <p className={classes.headerLabel}>Checkout</p>
                        <p className={classes.headerTitle}>{title || `Subscribe to ${productLabel}${plan.name}`}</p>
                    </div>
                    {step !== 'processing' && step !== 'threeDSecure' && (
                        <IconButton
                            aria-label="Close checkout"
                            className={classes.closeButton}
                            onClick={handleCancel}
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </div>

                {step === 'threeDSecure' ? (
                    <ThreeDSecureView actionURL={actionURL} />
                ) : step === 'processing' ? (
                    <ProcessingView />
                ) : (
                    <CheckoutForm
                        state={state}
                        plan={plan}
                        showBasePrice={showBasePrice}
                        showPromo={showPromo}
                        addOnsOnly={addOnsOnly}
                        payLabel={payLabel}
                    />
                )}
            </Drawer>
        </React.Fragment>
    );
}

CheckoutDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    plan: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        code: PropTypes.string,
        chargifyCode: PropTypes.string,
        price: PropTypes.shape({
            monthly: PropTypes.number,
            currency: PropTypes.string,
        }),
        addOns: PropTypes.array,
    }),
    addOnValues: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    // Product-specific overrides — default to the original Connect add-on behavior.
    subscribeAction: PropTypes.func,
    onSubscribed: PropTypes.func,
    previewAction: PropTypes.func,
    addOnsOnly: PropTypes.bool,
    threeDSecureStore: PropTypes.object,
    finalize3DSecureAction: PropTypes.func,
    title: PropTypes.string,
    productLabel: PropTypes.string,
    showBasePrice: PropTypes.bool,
    showPromo: PropTypes.bool,
    payLabel: PropTypes.string,
    onScheduleConflict: PropTypes.func,
};

CheckoutDrawer.defaultProps = {
    addOnValues: {},
    addOnsOnly: false,
    productLabel: 'Connect ',
    showBasePrice: true,
    showPromo: true,
    payLabel: 'ACTIVATE CONNECT',
};

const ObservedCheckoutDrawer = observer(CheckoutDrawer);
export default ObservedCheckoutDrawer;
